import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { XMarkIcon } from "@heroicons/react/24/outline";

import { apiErrorMessage } from "../../services/api";
import { drilldownReport } from "../../services/reportService";
import { formatAmount, formatCell } from "./reportFormat";

/** Figures sit on one line, end-aligned, so they read down a column. */
const FIGURES = ["money", "count", "percent", "duration"];
const isFigure = (column) => FIGURES.includes(column.type) || column.align === "end";

/** Text may wrap (a client name, a list of services); figures and dates may not. */
const cellClass = (column) =>
  isFigure(column)
    ? "whitespace-nowrap text-end tabular-nums"
    : column.type === "date"
      ? "whitespace-nowrap text-start tabular-nums"
      : "min-w-[5rem] max-w-[16rem] break-words text-start";

/**
 * The rows behind one chart point or table row (AC01).
 *
 * Opened from the URL (`?drill=day:2026-09-03`), so Back closes it and the
 * report underneath keeps its filters. Same filters and server-side scope as
 * the report; client names only appear when the server sends them.
 */
export default function DrilldownDrawer({ reportKey, point, label, filters, onClose }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setData(await drilldownReport(reportKey, point, filters, page));
    } catch (err) {
      setError(apiErrorMessage(err, t("reports.loadFailed")));
    } finally {
      setLoading(false);
    }
    // filters is a fresh object per render of the parent; its JSON is the identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportKey, point, page, JSON.stringify(filters), i18n.language, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [point]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);

    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const columns = data?.table?.columns ?? [];
  const rows = data?.table?.rows ?? [];
  const pagination = data?.table?.pagination;
  // With a Currency column beside them, money cells need not repeat the code
  // ("25.00", not "JOD 25.00"): the room goes to the text columns.
  const hasCurrency = columns.some((column) => column.key === "currency");
  const show = (value, column) =>
    hasCurrency && column.type === "money" && value && typeof value === "object"
      ? formatAmount(value.amount, locale)
      : formatCell(value, column.type, locale, t);

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/40"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="drilldown-title"
        className="flex h-full w-full flex-col bg-surface shadow-e2 sm:w-[min(960px,90vw)]"
      >
        <header className="flex items-start justify-between gap-3 border-b border-line px-5 py-4">
          <div className="min-w-0">
            <h2 id="drilldown-title" className="text-section-title font-bold">
              {t("reports.shell.detailTitle")}
            </h2>
            <p className="break-words text-dense text-ink-2">
              <bdi>{label ? formatCell(label, "text", locale, t) : ""}</bdi>
            </p>
            {/* Actions on the point, e.g. open the appointment to take payment (R07). */}
            {(data?.links ?? [])
              .filter((link) => typeof link.path === "string" && link.path.startsWith("/"))
              .map((link) => (
                <Link key={link.key} to={link.path} className="btn-secondary mt-2 inline-flex">
                  {link.label}
                </Link>
              ))}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("common.close")}
            className="grid h-9 w-9 place-items-center rounded-full hover:bg-raised"
          >
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </header>

        {/* Scrolls sideways only when the columns truly do not fit. */}
        <div className="min-h-0 flex-1 overflow-auto">
          {loading ? (
            <div className="space-y-2 p-5">
              {Array.from({ length: 8 }, (_, i) => (
                <div key={i} className="h-8 animate-pulse rounded bg-raised" />
              ))}
            </div>
          ) : error ? (
            <div className="p-5">
              <div className="rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-dense text-danger-text">
                {error}
              </div>
              <button type="button" className="btn-secondary mt-3" onClick={load}>
                {t("reports.shell.retry")}
              </button>
            </div>
          ) : rows.length === 0 ? (
            <p className="p-10 text-center text-dense text-ink-2">{t("reports.shell.noDetail")}</p>
          ) : (
            <table className="w-full text-dense">
              <thead className="sticky top-0 bg-raised text-ink-2">
                <tr>
                  {columns.map((column) => (
                    // A header may wrap ("Known processor fees"): the column is as wide as its values.
                    <th
                      key={column.key}
                      scope="col"
                      className={`px-3 py-2.5 align-bottom font-semibold ${
                        isFigure(column) ? "text-end" : "text-start"
                      }`}
                    >
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {rows.map((row, index) => (
                  <tr key={row.id ?? row.reference ?? index}>
                    {columns.map((column) => (
                      <td key={column.key} className={`px-3 py-2.5 align-top ${cellClass(column)}`}>
                        {/* Isolated, so a value never reorders its neighbours: a
                            figure reads left to right, a date or a name in its own direction. */}
                        <bdi dir={isFigure(column) ? "ltr" : "auto"}>{show(row[column.key], column)}</bdi>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {pagination && (
          <footer className="flex items-center justify-between border-t border-line px-5 py-3 text-dense text-ink-2">
            <span>{t("reports.shell.resultsCount", { count: pagination.total })}</span>
            {pagination.last_page > 1 && (
              <div className="flex gap-2">
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  {t("reports.shell.previousPage")}
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={page >= pagination.last_page}
                  onClick={() => setPage((p) => p + 1)}
                >
                  {t("reports.shell.nextPage")}
                </button>
              </div>
            )}
          </footer>
        )}
      </aside>
    </div>
  );
}
